import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MapPin, ArrowRight, Loader2, PlusCircle, ChevronDown, AlertTriangle, Building2, FileDown } from "lucide-react";

function LocationField({ locations, value, onChange, testId, placeholder, onResolve }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = locations.find((l) => l.key === value);
  const canAdd = q.includes(",");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className="w-full flex items-center justify-between rounded-xl border bg-white px-4 py-3.5 text-left hover:border-[#F5A623] transition-colors exec-shadow"
        >
          <div className="flex items-center gap-3 min-w-0">
            <MapPin className="h-5 w-5 text-essred shrink-0" />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{placeholder}</div>
              <div className="font-display font-semibold text-slate-900 truncate">{sel ? sel.label : "Select city, country"}</div>
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] bg-white" align="start">
        <Command>
          <CommandInput value={q} onValueChange={setQ} placeholder="Type any city — or 'City, Country'…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>Type "City, Country" then choose "Add" to resolve any world city.</CommandEmpty>
            <CommandGroup>
              {locations.map((l) => (
                <CommandItem key={l.key} value={l.label}
                  data-testid={`location-option-${l.key}`}
                  onSelect={() => { onChange(l.key); setOpen(false); }}>
                  <MapPin className="h-4 w-4 mr-2 text-essred" />
                  <span className="flex-1">{l.label}</span>
                  <span className="text-xs text-slate-500 font-mono">{l.currency}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {canAdd && (
              <CommandGroup heading="Add any world location">
                <CommandItem value={q} data-testid="location-add-any"
                  onSelect={async () => { const key = await onResolve(q); if (key) { onChange(key); setOpen(false); setQ(""); } }}>
                  <PlusCircle className="h-4 w-4 mr-2 text-[#B45309]" />
                  <span>Use “{q}”</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function Calculator() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(user?.employee_id || "");
  const [baseKey, setBaseKey] = useState("");
  const [hostKey, setHostKey] = useState("");
  const [saOpen, setSaOpen] = useState(false);
  const [sa, setSa] = useState({ amount: "", currency: "USD", frequency: "monthly", reason: "", start_date: "", end_date: "" });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [levelOptions, setLevelOptions] = useState([]);
  const [gradeBand, setGradeBand] = useState("");
  const [reverseAmt, setReverseAmt] = useState("");
  const [flexBase, setFlexBase] = useState(false);
  const [targetBasis, setTargetBasis] = useState("ctc");
  const [cmp, setCmp] = useState(null);
  const [savingPdf, setSavingPdf] = useState(false);
  const [bonusPct, setBonusPct] = useState("");
  const compare = async () => {
    if (!employeeId || !hostKey) return;
    try { const { data } = await api.post("/compare-plans", { employee_id: employeeId, base_key: baseKey, host_key: hostKey }); setCmp(data); } catch {}
  };
  const saveComparePdf = async () => {
    if (!employeeId || !baseKey || !hostKey) return;
    setSavingPdf(true);
    try {
      const resp = await fetch(`${API}/compare-plans/pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee_id: employeeId, base_key: baseKey, host_key: hostKey }),
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Mesabi_TierComparison_${employeeId}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } finally { setSavingPdf(false); }
  };

  const isEmployee = user?.role === "employee";

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/meta").then((r) => setLevelOptions(r.data.level_options || []));
    api.get("/employees").then((r) => {
      setEmployees(r.data);
      if (isEmployee && r.data[0]) {
        setEmployeeId(r.data[0].employee_id);
        setBaseKey(r.data[0].home_key || "");
      }
    });
  }, [isEmployee]);

  useEffect(() => {
    const emp = employees.find((e) => e.employee_id === employeeId);
    if (emp?.home_key) setBaseKey(emp.home_key);
  }, [employeeId, employees]);

  const run = async () => {
    setLoading(true); setPending(null);
    try {
      const body = { employee_id: employeeId, base_key: baseKey, host_key: hostKey };
      if (gradeBand) body.grade_band = gradeBand;
      if (bonusPct && Number(bonusPct) > 0) body.bonus_of_base_pct = Number(bonusPct) / 100;
      if (sa.amount && Number(sa.amount) > 0) body.special_allowance = { ...sa, amount: Number(sa.amount) };
      let data;
      if (reverseAmt && Number(reverseAmt) > 0) {
        const hostCcy = locations.find((l) => l.key === hostKey)?.currency;
        ({ data } = await api.post("/reverse", { ...body, target_ctc: Number(reverseAmt), target_currency: hostCcy, mode: flexBase ? "flex_base" : "proportional", target_basis: targetBasis }));
      } else {
        ({ data } = await api.post("/calculate", body));
      }
      if (data.status === "pending") setPending(data);
      else nav(`/assignment/${data.calculation_id}`);
    } catch (e) {
      setPending({ message: e.response?.data?.detail || "Calculation failed." });
    } finally { setLoading(false); }
  };

  const currencies = ["USD", "INR", "GBP", "SGD", "AED", "CHF", "JPY", "NGN", "EUR"];
  const selEmp = employees.find((e) => e.employee_id === employeeId);

  const handleResolve = async (query) => {
    const parts = query.split(",");
    const city = parts[0]?.trim();
    const country = parts.slice(1).join(",").trim();
    if (!city || !country) return null;
    try {
      const { data } = await api.post("/locations/resolve", { city, country });
      setLocations((prev) => (prev.some((l) => l.key === data.key) ? prev : [...prev, data]));
      return data.key;
    } catch { return null; }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto animate-fade-up">
      <div className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B45309] font-semibold">Home-Based Balance Sheet</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Assignment Calculator</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Provide only three inputs. All compensation data is loaded securely from the employee master —
        the calculated host compensation is the output of the model, never a pre-set target.
      </p>

      {!isEmployee && (
        <div className="mt-6">
          <Label>Employee (from secure master)</Label>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger data-testid="calc-employee-select" className="mt-1.5 bg-white">
              <SelectValue placeholder="Select employee" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {employees.map((e) => (
                <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`emp-option-${e.employee_id}`}>
                  {e.name} · {e.employee_id} · {e.grade || "—"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {selEmp && (
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 border px-4 py-3 text-sm" data-testid="calc-profile-summary">
          <Building2 className="h-4 w-4 text-slate-900" />
          <span className="text-slate-500">Home payroll:</span>
          <span className="font-mono font-semibold text-slate-900">
            {selEmp.profile?.home_currency ? fmt(selEmp.profile.home_base_salary, selEmp.profile.home_currency) : "secured"}
          </span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-500">{selEmp.business_unit}</span>
        </div>
      )}

      <div className="mt-4">
        <Label>Assignment Level / Plan Tier</Label>
        <Select value={gradeBand} onValueChange={setGradeBand}>
          <SelectTrigger data-testid="calc-level-select" className="mt-1.5 bg-white"><SelectValue placeholder="Auto from employee grade" /></SelectTrigger>
          <SelectContent className="bg-white">
            {levelOptions.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`level-${o.value}`}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 grid gap-4">
        {/* Input 1 & 2 */}
        <div className="grid sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
          <LocationField locations={locations} value={baseKey} onChange={setBaseKey} onResolve={handleResolve}
            testId="calc-base-location-input" placeholder="Base location" />
          <div className="hidden sm:flex h-10 w-10 rounded-full bg-slate-50 text-slate-500 items-center justify-center mx-auto">
            <ArrowRight className="h-5 w-5" />
          </div>
          <LocationField locations={locations} value={hostKey} onChange={setHostKey} onResolve={handleResolve}
            testId="calc-host-location-input" placeholder="Host location" />
        </div>

        {/* Input 3: Special allowance */}
        <Collapsible open={saOpen} onOpenChange={setSaOpen} className="rounded-xl border bg-white exec-shadow">
          <CollapsibleTrigger asChild>
            <button data-testid="calc-special-allowance-input" className="w-full flex items-center justify-between px-4 py-3.5 text-left">
              <div className="flex items-center gap-3">
                <PlusCircle className="h-5 w-5 text-[#B45309]" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Special allowance (optional)</div>
                  <div className="font-display font-semibold text-slate-900">
                    {sa.amount && Number(sa.amount) > 0 ? `${fmt(sa.amount, sa.currency)} / ${sa.frequency}` : "Default: zero"}
                  </div>
                </div>
              </div>
              <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${saOpen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t">
              <div>
                <Label>Amount</Label>
                <Input data-testid="sa-amount" type="number" value={sa.amount} className="mt-1.5"
                  onChange={(e) => setSa({ ...sa, amount: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={sa.currency} onValueChange={(v) => setSa({ ...sa, currency: v })}>
                  <SelectTrigger data-testid="sa-currency" className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={sa.frequency} onValueChange={(v) => setSa({ ...sa, frequency: v })}>
                  <SelectTrigger data-testid="sa-frequency" className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["monthly", "quarterly", "annual", "one_time"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason</Label>
                <Input data-testid="sa-reason" value={sa.reason} className="mt-1.5"
                  onChange={(e) => setSa({ ...sa, reason: e.target.value })} placeholder="e.g. security detail" />
              </div>
              <div>
                <Label>Start date</Label>
                <Input data-testid="sa-start" type="date" value={sa.start_date} className="mt-1.5"
                  onChange={(e) => setSa({ ...sa, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End date</Label>
                <Input data-testid="sa-end" type="date" value={sa.end_date} className="mt-1.5"
                  onChange={(e) => setSa({ ...sa, end_date: e.target.value })} />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <div className="mt-4 panel p-4" data-testid="bonus-panel">
        <span className="term-label text-[#B45309]">Assignment Bonus (optional)</span>
        <Label className="text-xs block mt-2">Bonus as % of Home Base Salary</Label>
        <div className="flex items-center gap-2 mt-1.5 max-w-xs">
          <Input type="number" data-testid="bonus-pct-input" value={bonusPct} onChange={(e) => setBonusPct(e.target.value)} placeholder="e.g. 20" />
          <span className="text-sm text-slate-500 font-mono-fin">% of base</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Adds a base-linked cash bonus — flows into employee net cash, saving potential and total employer cost.</p>
      </div>

      <div className="mt-4 rounded-xl border bg-white p-4" data-testid="reverse-solve">
        <span className="term-label">Reverse Solve (optional)</span>
        <div className="mt-2 flex gap-1.5" data-testid="reverse-basis-toggle">
          {[["ctc", "Employer Cost (CTC)"], ["net", "Employee Net (take-home)"]].map(([v, lbl]) => (
            <button key={v} type="button" data-testid={`reverse-basis-${v}`} onClick={() => setTargetBasis(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${targetBasis === v ? "bg-[#D97706] text-white border-[#D97706]" : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"}`}>{lbl}</button>
          ))}
        </div>
        <Label className="text-xs block mt-3">Target {targetBasis === "net" ? "Employee Net take-home" : "Total Employer Cost / CTC"} in {locations.find((l) => l.key === hostKey)?.currency || "host currency"}</Label>
        <Input type="number" data-testid="reverse-ctc-input" value={reverseAmt} onChange={(e) => setReverseAmt(e.target.value)} placeholder="e.g. 400000" className="mt-1.5 max-w-xs" />
        <p className="text-xs text-muted-foreground mt-1">If set, the model back-solves the other figures proportionally to hit this {targetBasis === "net" ? "net take-home" : "CTC"} (labelled user-provided; requires review).</p>
        <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="reverse-flexbase" checked={flexBase} onChange={(e) => setFlexBase(e.target.checked)} />
          Hold housing &amp; education fixed — flex only base + bonus
        </label>
      </div>

      <div className="mt-4">
        <div className="flex flex-wrap gap-2">
          <Button type="button" data-testid="compare-plans-btn" onClick={compare} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">Compare all tiers</Button>
          {cmp?.plans && (
            <Button type="button" data-testid="compare-pdf-btn" onClick={saveComparePdf} disabled={savingPdf} variant="outline" className="border-gold/60 text-[#B45309] hover:bg-gold/10">
              {savingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}Save comparison PDF
            </Button>
          )}
        </div>
        {cmp?.plans && (
          <div className="mt-3 overflow-x-auto rounded-xl border bg-white" data-testid="plan-comparison-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-3 py-2">Tier</th><th className="text-right px-3 py-2">Rent cap</th><th className="text-right px-3 py-2">Employer Cost (USD)</th><th className="text-right px-3 py-2">Employee Net</th></tr></thead>
              <tbody>{cmp.plans.map((p) => (
                <tr key={p.plan} className="border-t" data-testid={`cmp-${p.plan}`}>
                  <td className="px-3 py-2 text-slate-700">{p.label}</td>
                  <td className="px-3 py-2 text-right font-mono">${p.rent_cap}/mo</td>
                  <td className="px-3 py-2 text-right font-mono text-[#B45309]">{fmt(p.tec_usd, "USD")}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(p.net_host, p.currency)}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      </div>

      {pending && (
        <div data-testid="calc-pending-notice" className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900">{pending.message}</div>
            {pending.missing_fields && (
              <div className="mt-2 text-sm text-amber-800">
                Missing: <span className="font-mono">{pending.missing_fields.join(", ")}</span>
                <div className="mt-2 text-amber-700">An HR administrator must complete these in the Admin Console before this calculation can run.</div>
              </div>
            )}
          </div>
        </div>
      )}

      <Button data-testid="calc-run-button" onClick={run}
        disabled={loading || !employeeId || !baseKey || !hostKey || baseKey === hostKey}
        className="mt-6 h-12 px-8 bg-[#C8102E] hover:bg-[#A00D25] text-white font-semibold text-base">
        {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
        Calculate Assignment Compensation
      </Button>
      {baseKey && hostKey && baseKey === hostKey && (
        <p className="mt-2 text-sm text-[#C8102E]">Base and host locations must differ.</p>
      )}
    </div>
  );
}
